import React from 'react';
import { array, bool, func, object, string } from 'prop-types';
import classNames from 'classnames';

// Import configs and util modules
import { propTypes } from '../../util/types';
import { FormattedMessage } from '../../util/reactIntl';
import { LISTING_STATE_DRAFT } from '../../util/types';

// Import shared components
import { ListingLink } from '../../components';
import { EditLiveRoomGalleryForm } from '../../forms';

// Import modules from this directory
import css from './EditLiveRoomGalleryPanel.module.css';

const getInitialValues = params => {
  const { images, videos } = params;
  return { images, videos };
};

// This function denormalizes the video data by removing the 'file' and 'videoId' properties
// and retaining only the attributes that should be saved within a listing.
// The resulting array of videos is then sent to the API to be added to the publicData of a listing.
const denormaliseVideos = videos =>
  videos ? videos.map(({ file, videoId, ...video }) => video) : [];

const EditLiveRoomGalleryPanel = props => {
  const {
    className,
    rootClassName,
    errors,
    disabled,
    ready,
    listing,
    onImageUpload,
    onVideoUpload,
    submitButtonText,
    panelUpdated,
    updateInProgress,
    promoteImageInProgress,
    promoteImageError,
    showListingInProgress,
    onSubmit,
    onRemoveImage,
    onRemoveVideo,
    onPromoteImage,
    onPromoteVideo,
    promoteVideoInProgress,
    promoteVideoError,
    onGetPrevPathParams,
  } = props;

  const rootClass = rootClassName || css.root;
  const classes = classNames(rootClass, className);

  const isPublished = listing.id && listing.attributes.state !== LISTING_STATE_DRAFT;

  // images title
  const unpublishedPanelTitleImages = (
    <FormattedMessage id="EditLiveRoomGalleryPanel.createListingTitle" />
  );
  const publishedPanelTitleImages = (
    <FormattedMessage
      id="EditLiveRoomGalleryPanel.title"
      values={{
        listingTitle: <ListingLink className={css.listingLink} listing={listing} isLiveRoom />,
      }}
    />
  );

  // videos title
  const unpublishedPanelTitleVideos = (
    <FormattedMessage id="EditLiveRoomGalleryPanel.createListingVideosTitle" />
  );
  const publishedPanelTitleVideos = (
    <FormattedMessage
      id="EditLiveRoomGalleryPanel.videosTitle"
      values={{
        listingTitle: <ListingLink className={css.listingLink} listing={listing} isLiveRoom />,
      }}
    />
  );

  const panelTitleImages = isPublished ? publishedPanelTitleImages : unpublishedPanelTitleImages;
  const panelTitleVideos = isPublished ? publishedPanelTitleVideos : unpublishedPanelTitleVideos;

  const panelTitle = {
    images: panelTitleImages,
    videos: panelTitleVideos,
  };

  return (
    <div className={classes}>
      <EditLiveRoomGalleryForm
        className={css.form}
        disabled={disabled}
        ready={ready}
        fetchErrors={errors}
        panelTitle={panelTitle}
        initialValues={getInitialValues(props)}
        onImageUpload={onImageUpload}
        onVideoUpload={onVideoUpload}
        onSubmit={values => {
          const { addImage, addVideo, videos, ...updateValues } = values;
          const updatedValues = {
            ...updateValues,
            publicData: {
              videos: denormaliseVideos(videos),
            },
          };
          onSubmit(updatedValues);
        }}
        onRemoveImage={onRemoveImage}
        onRemoveVideo={onRemoveVideo}
        onPromoteImage={onPromoteImage}
        onPromoteVideo={onPromoteVideo}
        promoteVideoInProgress={promoteVideoInProgress}
        promoteVideoError={promoteVideoError}
        onGetPrevPathParams={onGetPrevPathParams}
        saveActionMsg={submitButtonText}
        updated={panelUpdated}
        updateInProgress={updateInProgress}
        promoteImageInProgress={promoteImageInProgress}
        promoteImageError={promoteImageError}
        showListingInProgress={showListingInProgress}
        listing={listing}
      />
    </div>
  );
};

EditLiveRoomGalleryPanel.defaultProps = {
  className: null,
  rootClassName: null,
  errors: null,
  images: [],
  listing: null,
  promoteImageInProgress: false,
  promoteImageError: null,
  promoteVideoInProgress: false,
  promoteVideoError: null,
  showListingInProgress: false,
};

EditLiveRoomGalleryPanel.propTypes = {
  className: string,
  rootClassName: string,
  errors: object,
  disabled: bool.isRequired,
  ready: bool.isRequired,
  images: array,
  updateInProgress: bool.isRequired,
  promoteImageInProgress: bool.isRequired,
  promoteImageError: propTypes.error,

  // We cannot use propTypes.listing since the listing might be a draft.
  listing: object,

  onImageUpload: func.isRequired,
  onVideoUpload: func.isRequired,
  onSubmit: func.isRequired,
  submitButtonText: string.isRequired,
  panelUpdated: bool.isRequired,
  updateInProgress: bool.isRequired,
  showListingInProgress: bool.isRequired,
  onRemoveImage: func.isRequired,
  onRemoveVideo: func.isRequired,
  onPromoteImage: func.isRequired,
  onPromoteVideo: func.isRequired,
  promoteVideoInProgress: bool.isRequired,
  promoteVideoError: propTypes.error,
  onGetPrevPathParams: func.isRequired,
};

export default EditLiveRoomGalleryPanel;
